import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { API_URL } from "../config/api";
import {
  Anchor, AlertCircle, Lock, Zap, Loader2, CheckCircle2, X,
} from "lucide-react";

/**
 * CMA CGM SpotOn live-rate check panel.
 *
 * Slots inside the "Create Offer from Scratch" dialog. Auto-collapses
 * until the user clicks "Check CMA Live Rate" — at that point it
 * validates POL/POD/container/cargo_ready_date, calls the backend,
 * and renders the best offer + surcharges + Lock-My-Price flag.
 *
 * Props:
 *   standaloneData   — current form state (POL, POD, container, etc.)
 *   onApplyAsCost    — fn(rate, currency) called when user clicks
 *                      "Apply CMA rate as cost". Lets the parent
 *                      auto-fill the purchase/cost component.
 */
export default function CMASpotOnPanel({ standaloneData, onApplyAsCost, onApplyWithMargin }) {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [marginType, setMarginType] = useState("percent");
  const [marginValue, setMarginValue] = useState(15);
  const [rateLimited, setRateLimited] = useState(false);

  // Only ever send CMA real UN/LOCODEs — never free-typed text (which CMA
  // rejects with POR_ERR "Unknown place"). If a port wasn't picked from the
  // mapped dropdown, its *_code is empty and the check stays disabled.
  const polCode = standaloneData.pol_code || "";
  const podCode = standaloneData.pod_code || "";
  const containerType =
    standaloneData.container_type ||
    standaloneData.containers?.[0]?.type ||
    "40HC";
  const containerQty =
    standaloneData.container_quantity ||
    standaloneData.containers?.reduce((s, c) => s + (Number(c.quantity) || 1), 0) ||
    1;
  const cargoReadyDate = standaloneData.cargo_ready_date;

  // A POL/POD was typed but not selected from the mapped dropdown.
  const polUnmapped = !polCode && !!(standaloneData.pol);
  const podUnmapped = !podCode && !!(standaloneData.pod);
  const ready = polCode && podCode && containerType && cargoReadyDate;

  // Map UI container types → CMA CGM SpotOn equipment codes (verified live:
  // SpotOn uses friendly size+type codes, not ISO 6346 group codes).
  const isoEquipment = (t) => {
    const k = String(t || "").toUpperCase();
    const map = {
      "20GP": "20GP", "40GP": "40GP", "40HC": "40HC", "45HC": "45HC", "20HC": "20HC",
      "20RF": "20RE", "20RE": "20RE", "40RF": "40RH", "40RH": "40RH", "40RHC": "40RH",
      // legacy / ISO aliases
      "22GP": "20GP", "42GP": "40GP", "45GP": "40HC", "L5GP": "45HC",
    };
    if (map[k]) return map[k];
    if (k.includes("REEF") || k.includes("RF") || k.includes("RE")) return k.includes("20") ? "20RE" : "40RH";
    if (k.includes("45")) return "45HC";
    if (k.includes("HC") || k.includes("HIGH")) return "40HC";
    if (k.includes("20")) return "20GP";
    return "40GP";
  };

  const checkRate = async () => {
    setLoading(true);
    setError(null);
    setNotConfigured(false);
    setRateLimited(false);
    setResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/cma-cgm/spot/search`,
        {
          portOfLoading: polCode,
          portOfDischarge: podCode,
          // Pre-carriage / on-carriage: only sent when a MAPPED inland/port code
          // exists. Free-typed places (no code) are omitted so CMA prices the
          // port-to-port leg and we surface a "port-to-port only" notice.
          placeOfOrigin: standaloneData.poa_code || undefined,
          placeOfDelivery: standaloneData.pofd_code || undefined,
          // Commodity code from the picker (defaults to FAK = Freight All Kinds).
          commodityCode: standaloneData.commodity_code || "FAK",
          departureDate: cargoReadyDate || null,
          hazardous: !!standaloneData.is_imo,
          requestedEquipments: [{
            equipmentGroupIsoCode: isoEquipment(containerType),
            numberOfContainers: Number(containerQty) || 1,
            weightPerContainer: Number(standaloneData.weight_kg) || 18000,
          }],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Normalise into the shape this panel renders (best_offer/offers).
      const data = {
        ...res.data,
        container_type: containerType,
        container_quantity: containerQty,
        sandbox: false,
      };
      setResult(data);
      if (!data.offer_count) {
        toast.warning("CMA returned no SpotOn offers for this lane/date");
      } else {
        toast.success(`CMA: ${data.offer_count} offers · best ${data.best_offer.total_amount} ${data.best_offer.currency}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 503) {
        setNotConfigured(true);
        setError(typeof detail === "string" ? detail : (detail?.message || "CMA pricing API not configured"));
      } else if (e.response?.status === 403) {
        setRateLimited(true);
        setError(typeof detail === "string" ? detail : "CMA CGM temporarily blocked this request (edge rate-limit). Please retry in a couple of minutes.");
      } else if (typeof detail === "string") {
        setError(detail);
      } else if (detail?.upstream_status) {
        setError(`CMA upstream ${detail.upstream_status}: ${detail.upstream_body || ""}`);
      } else {
        setError(e.message || "Request failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const computeSell = (cost) => {
    const c = Number(cost) || 0;
    const m = Number(marginValue) || 0;
    return marginType === "percent" ? c * (1 + m / 100) : c + m;
  };

  const applyWithMargin = () => {
    const b = result?.best_offer;
    if (!b) return;
    const oceanFreight = Number(b.basic_ocean_freight) || Number(b.total_amount) || 0;
    const sell = Math.round(computeSell(oceanFreight) * 100) / 100;
    onApplyWithMargin?.({
      currency: b.currency,
      oceanFreight,
      oceanFreightSell: sell,
      allIn: Number(b.total_amount) || 0,
      marginType,
      marginValue: Number(marginValue) || 0,
      additionalCharges: b.additional_charges || [],
      includedCharges: (b.included_charges || []).map((c) => c.label),
      carrier: "CMA CGM",
      service: b.service,
      vessel: b.vessel,
      transitDays: b.transit_days,
      etd: b.etd,
      eta: b.eta,
      containerType: result.container_type,
      quantity: result.container_quantity,
    });
  };

  return (
    <div
      className="mt-4 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 p-4"
      data-testid="cma-spoton-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-blue-600 p-1.5">
            <Anchor className="h-4 w-4 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">CMA CGM Live Rate</h4>
            <p className="text-xs text-slate-500">
              Cross-check your manual offer against CMA SpotOn instant quote
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={checkRate}
          disabled={!ready || loading}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="cma-check-rate-btn"
          title={ready ? "Fetch live CMA SpotOn rate" : "Fill POL, POD, container type and cargo ready date"}
        >
          {loading ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Checking…</>
          ) : (
            <><Zap className="h-3.5 w-3.5 mr-1.5" /> Check CMA Live Rate</>
          )}
        </Button>
      </div>

      {!ready && (
        <p className="mt-3 text-xs text-amber-700 inline-flex items-center gap-1" data-testid="cma-not-ready">
          <AlertCircle className="h-3.5 w-3.5" />
          {(polUnmapped || podUnmapped)
            ? `Select ${[polUnmapped && "POL", podUnmapped && "POD"].filter(Boolean).join(" & ")} from the dropdown (a mapped port) — free-typed places aren't accepted by CMA.`
            : "Need POL · POD · Container type · Cargo Ready Date to check."}
        </p>
      )}

      {notConfigured && (
        <div className="mt-3 rounded bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="cma-not-configured">
          <div className="font-semibold mb-1">CMA pricing API not yet provisioned.</div>
          {error}
          <div className="mt-2 text-amber-800">
            CMA CGM SpotOn isn't returning a token/scope yet. Check{" "}
            <strong>Integration Health → CMA CGM</strong> (or the CMA CGM page
            “Re-test” button) — the <code>instantquote</code> and{" "}
            <code>location</code> scopes must be active on the API app.
          </div>
        </div>
      )}

      {rateLimited && (
        <div className="mt-3 rounded bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="cma-rate-limited">
          <div className="font-semibold mb-1">CMA CGM temporarily unavailable</div>
          {error}
        </div>
      )}

      {error && !notConfigured && !rateLimited && (
        <div className="mt-3 rounded bg-red-50 border border-red-200 p-2 text-xs text-red-700 flex items-start gap-1.5">
          <X className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && result.best_offer && (
        <div className="mt-4 space-y-3" data-testid="cma-result">
          {/* Headline best offer card */}
          <div className="rounded-lg border border-blue-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs text-slate-500">CMA SpotOn — Best Offer</div>
                <div className="text-2xl font-bold text-slate-900 mt-0.5">
                  {result.best_offer.total_amount?.toLocaleString()}{" "}
                  <span className="text-base font-semibold text-slate-500">
                    {result.best_offer.currency}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {result.pol} → {result.pod} · {result.container_type} · {result.container_quantity} unit(s)
                  {result.best_offer.transit_days && ` · ${result.best_offer.transit_days}d transit`}
                  {result.sandbox && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-medium">SANDBOX</span>
                  )}
                </div>
                {(result.pol_point_code || result.pod_point_code) && (
                  <div className="text-[11px] text-slate-400 mt-0.5" data-testid="cma-point-codes">
                    Point codes: {result.pol_point_code || result.pol} → {result.pod_point_code || result.pod}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5">
                {result.best_offer.lock_my_price_available && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium"
                    data-testid="cma-lock-my-price-badge"
                    title={`Lock this rate for ${result.best_offer.lock_duration_hours || 72}h`}
                  >
                    <Lock className="h-3 w-3" />
                    Lock-My-Price ({result.best_offer.lock_duration_hours || 72}h)
                  </span>
                )}
                {result.best_offer.validity_until && (
                  <span className="text-xs text-slate-500">
                    Valid until {String(result.best_offer.validity_until).slice(0, 10)}
                  </span>
                )}
                <Button
                  size="sm"
                  onClick={() => onApplyAsCost?.(result.best_offer.total_amount, result.best_offer.currency)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="cma-apply-as-cost-btn"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  Apply as cost
                </Button>
              </div>
            </div>

            {/* Routing */}
            {(result.best_offer.vessel || result.best_offer.service || result.best_offer.etd) && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {result.best_offer.vessel && (
                  <Stat label="Vessel" value={result.best_offer.vessel} />
                )}
                {result.best_offer.service && (
                  <Stat label="Service" value={result.best_offer.service} />
                )}
                {result.best_offer.etd && (
                  <Stat label="ETD" value={String(result.best_offer.etd).slice(0, 10)} />
                )}
                {result.best_offer.eta && (
                  <Stat label="ETA" value={String(result.best_offer.eta).slice(0, 10)} />
                )}
              </div>
            )}

            {/* Charge breakdown — mirrors CMA CGM's "All in rate" view */}
            <div className="mt-3 rounded-md border border-slate-100 bg-slate-50/60 p-2.5" data-testid="cma-charge-breakdown">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-700">Ocean Freight (all-in)</span>
                <span className="font-semibold text-slate-900" data-testid="cma-ocean-freight">
                  {result.best_offer.basic_ocean_freight?.toLocaleString()} {result.best_offer.currency}
                </span>
              </div>
              {result.best_offer.included_charges?.length > 0 && (
                <div className="mt-1.5 text-[11px] text-slate-500 leading-relaxed" data-testid="cma-included-charges">
                  <span className="font-medium text-slate-600">Included: </span>
                  {result.best_offer.included_charges.map((c) => c.label).join(" · ")}
                </div>
              )}

              {result.best_offer.additional_charges?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-200" data-testid="cma-additional-charges">
                  <div className="text-[11px] font-medium text-slate-600 mb-1">Charges payable at import</div>
                  <table className="w-full text-xs">
                    <tbody>
                      {result.best_offer.additional_charges.map((s, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="py-1 pr-2 text-slate-500">
                            {s.label}
                            {s.payment_method && <span className="text-slate-400"> ({s.payment_method})</span>}
                          </td>
                          <td className="py-1 text-right text-slate-900 font-medium whitespace-nowrap">
                            {s.amount?.toLocaleString()} {s.currency}
                            {s.pivot_amount != null && s.pivot_currency && s.pivot_currency !== s.currency && (
                              <span className="text-slate-400 font-normal"> (≈{s.pivot_amount.toLocaleString()} {s.pivot_currency})</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-700">All-in total per container</span>
                <span className="font-bold text-slate-900" data-testid="cma-allin-total">
                  {result.best_offer.total_amount?.toLocaleString()} {result.best_offer.currency}
                </span>
              </div>
            </div>
          </div>

          {/* Pre-carriage / on-carriage not covered by a port-to-port SpotOn rate */}
          {(standaloneData.poa || standaloneData.pofd) && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-900" data-testid="cma-port-to-port-notice">
              <span className="font-semibold">Port-to-port rate.</span> This CMA quote covers{" "}
              {result.pol || "POL"} → {result.pod || "POD"} only.
              {standaloneData.poa ? ` Pre-carriage from ${standaloneData.poa}` : ""}
              {standaloneData.poa && standaloneData.pofd ? " and" : ""}
              {standaloneData.pofd ? ` on-carriage to ${standaloneData.pofd}` : ""}
              {" "}is not included — please request door/ramp haulage as an additional service.
            </div>
          )}

          {/* Turn CMA rate into a customer offer line (cost + margin) */}
          {result.best_offer.available !== false && result.best_offer.total_amount > 0 && (
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3"
              data-testid="cma-margin-box"
            >
              <div className="text-xs font-semibold text-emerald-900 mb-2">
                Turn this into a customer offer
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-[11px] text-slate-500 block mb-0.5">Margin</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="0"
                      value={marginValue}
                      onChange={(e) => setMarginValue(e.target.value)}
                      className="w-20 h-9 rounded-md border border-slate-300 px-2 text-sm"
                      data-testid="cma-margin-value"
                    />
                    <select
                      value={marginType}
                      onChange={(e) => setMarginType(e.target.value)}
                      className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
                      data-testid="cma-margin-type"
                    >
                      <option value="percent">%</option>
                      <option value="fixed">{result.best_offer.currency} flat</option>
                    </select>
                  </div>
                </div>
                <div className="text-sm">
                  <div className="text-[11px] text-slate-500">Ocean Freight → Sell (per ctr)</div>
                  <div className="font-semibold text-slate-900" data-testid="cma-sell-preview">
                    {(result.best_offer.basic_ocean_freight || result.best_offer.total_amount)?.toLocaleString()} →{" "}
                    {(Math.round(computeSell(result.best_offer.basic_ocean_freight || result.best_offer.total_amount) * 100) / 100).toLocaleString()}{" "}
                    {result.best_offer.currency}
                  </div>
                  <div className="text-[10px] text-slate-400">Destination &amp; service charges added as separate lines</div>
                </div>
                <Button
                  size="sm"
                  onClick={applyWithMargin}
                  className="bg-emerald-600 hover:bg-emerald-700 ml-auto"
                  data-testid="cma-apply-with-margin-btn"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  Apply CMA rate + margin
                </Button>
              </div>
            </div>
          )}

          {/* Other offers (compact) */}
          {result.offers.length > 1 && (
            <details>
              <summary className="cursor-pointer text-xs font-medium text-slate-700 px-1">
                +{result.offers.length - 1} more offer(s)
              </summary>
              <div className="mt-2 space-y-1">
                {result.offers.slice(1).map((o, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-xs px-3 py-1.5 bg-white rounded border border-slate-100"
                  >
                    <span className="text-slate-600">
                      {o.vessel || o.service || "—"}{o.transit_days ? ` · ${o.transit_days}d` : ""}
                    </span>
                    <span className="font-semibold text-slate-900">
                      {o.total_amount?.toLocaleString()} {o.currency}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded px-2 py-1.5">
      <div className="text-[10px] uppercase text-slate-500 tracking-wide">{label}</div>
      <div className="font-medium text-slate-900 truncate" title={value}>{value}</div>
    </div>
  );
}
